import type { CSSProperties } from 'react';
import { cn } from '@/lib/utils';
import './Skeleton.css';

export interface SkeletonProps {
  /** CSS width (e.g. "100%", 120). Defaults to 100%. */
  width?: string | number;
  /** CSS height (e.g. "1em", 16). Defaults to the line height. */
  height?: string | number;
  /** Round the shape fully (avatars, dots). */
  circle?: boolean;
  className?: string;
}

/**
 * Content-shaped loading placeholder. Use these in the shape of the content that
 * is loading — never a centered spinner inside content. Decorative to AT.
 */
export function Skeleton({ width, height, circle, className }: SkeletonProps): JSX.Element {
  const style: CSSProperties = {
    width: width ?? '100%',
    height: height ?? '1em',
    borderRadius: circle ? 'var(--r-full)' : undefined,
  };
  return <span className={cn('skeleton', className)} style={style} aria-hidden="true" />;
}

export interface SkeletonTextProps {
  /** Number of lines. */
  lines?: number;
  className?: string;
}

/** A paragraph of skeleton lines, the last one short, matching real prose shape. */
export function SkeletonText({ lines = 3, className }: SkeletonTextProps): JSX.Element {
  return (
    <span className={cn('skeleton-text', className)} aria-hidden="true">
      {Array.from({ length: lines }, (_, i) => (
        <Skeleton key={i} height="0.8em" width={i === lines - 1 ? '60%' : '100%'} />
      ))}
    </span>
  );
}
